import { ExternalEmailContact } from "@accurx/api/clinician-messaging";
import { useCurrentWorkspace } from "@accurx/auth";
import { Ds } from "@accurx/design";
import { useDirectorySearchQuery } from "domains/directory/hooks/queries/useDirectorySearchQuery";

import { ContactSelectCard } from "../ContactSelectCard/ContactSelectCard";
import {
    ContactListSkeleton,
    SkeletonText,
} from "../ContactSelectCard/ContactSelectCardSkeleton";

const FREQUENT_CONTACTS_LIMIT = 6;

export const FrequentContacts = () => {
    const { orgId: workspaceId } = useCurrentWorkspace();
    const { data, isLoading } = useDirectorySearchQuery({
        workspaceId,
        limit: FREQUENT_CONTACTS_LIMIT,
        sortBy: "MessageFrequency",
    });

    if (isLoading) {
        return (
            <>
                <Ds.Box pb="1">
                    <SkeletonText width="150px" />
                </Ds.Box>
                <ContactListSkeleton
                    count={FREQUENT_CONTACTS_LIMIT}
                    role="status"
                />
            </>
        );
    }

    if (data?.contacts.length) {
        return (
            <>
                <Ds.Box pb="1">
                    <Ds.Flex gap="0.5">
                        <Ds.Text as="h2" weight="bold" color="zinc">
                            Frequently contacted
                        </Ds.Text>
                        <Ds.Tooltip>
                            <Ds.Tooltip.Trigger>
                                <Ds.Icon name="Info" size="small" />
                            </Ds.Tooltip.Trigger>
                            <Ds.Tooltip.Content
                                side="bottom"
                                style={{
                                    zIndex: 1000,
                                    whiteSpace: "pre-line",
                                    textAlign: "center",
                                }}
                            >
                                {`By colleagues in your 
                                    workspace and nearby 
                                    workspaces`}
                            </Ds.Tooltip.Content>
                        </Ds.Tooltip>
                    </Ds.Flex>
                </Ds.Box>
                <ContactList contacts={data.contacts} />
            </>
        );
    }

    return null;
};

const ContactList = ({ contacts }: { contacts: ExternalEmailContact[] }) => {
    return (
        <Ds.Flex flexDirection="column" gap="1">
            {contacts.map((contact) => {
                const serviceName = contact.service?.name;
                const institutionName = contact.institution?.name;
                return (
                    <ContactSelectCard
                        contact={contact}
                        data-testid={contact.id}
                        key={contact.id}
                        contactOrigin="Frequently contacted"
                    >
                        <Ds.Text color="metal">
                            {institutionName}
                            {institutionName && serviceName && (
                                <>&nbsp;&bull;&nbsp;</>
                            )}
                            {serviceName}
                        </Ds.Text>
                    </ContactSelectCard>
                );
            })}
        </Ds.Flex>
    );
};
